import { Form } from 'formik';
import { Formik } from 'components/form/formik';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PaperclipIcon, XIcon } from '@phosphor-icons/react';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';

import { PageHeader } from '../../../components/PageHeader';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { InputField } from '../../../components/input/InputField';
import { TextAreaField } from '../../../components/textarea/TextAreaField';
import { Button } from '../../../components/button/Button';
import { Dropzone } from '../../../components/Dropzone';
import { SupplierComboboxField } from '../../supplier/SupplierComboboxField';
import { getDisplayError } from '../../../utils/get-display-error';
import { createClaim } from '../models';
import { useDownloadDocument } from '../../document/hooks/useDownloadDocument';
import { validateClaimForm } from '../constants';
import { handleClaimDocumentUpload } from '../hooks/useClaimDocumentUpload';

interface FormDocument {
  id: string;
  name: string;
  hash: string;
}

interface CreateClaimFormValues {
  counterParty: string;
  supplier: { id: number; name: string } | null;
  amount: string;
  info: string;
  attachments: FormDocument[];
}

const CreateClaimPage = () => {
  const navigate = useNavigate();
  const { downloadFile, downloadingFiles } = useDownloadDocument();

  const initialValues: CreateClaimFormValues = {
    counterParty: '',
    supplier: null,
    amount: '',
    info: '',
    attachments: [],
  };

  return (
    <>
      <PageHeader title="Nieuwe claim" />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Claims',
              to: '/internal/finance/claims',
            }}
            currentItem="Nieuwe claim"
          />
        }
      />

      <div className="px-4">
        <Formik
          initialValues={initialValues}
          validate={validateClaimForm}
          onSubmit={async (values) => {
            try {
              const amountInCents = Math.round(parseFloat(values.amount) * 100);

              await createClaim({
                counterParty: values.counterParty,
                supplierId: values.supplier ? values.supplier.id : null,
                amount: amountInCents,
                info: values.info,
                documentIds: values.attachments.map((a) => a.id),
              });

              trackEvent('claim_created', {
                supplier_id: values.supplier?.id ?? null,
                amount_cents: amountInCents,
                document_count: values.attachments.length,
              });
              toast.success('Claim aangemaakt');
              navigate('..');
            } catch (err) {
              captureException(err);
              toast.error(`Kon claim niet aanmaken: ${getDisplayError(err)}`);
            }
          }}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              <div className="max-w-2xl flex flex-col gap-4">
                <InputField labelText="Tegenpartij" name="counterParty" type="text" />

                <SupplierComboboxField name="supplier" labelText="Onderaannemer" isNullable={true} />

                <InputField labelText="Bedrag (€)" name="amount" type="number" step="0.01" />

                <TextAreaField labelText="Info" name="info" rows={5} />

                {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-fg-secondary mb-2">Bijlagen</label>

                  <div className="mb-4">
                    <Dropzone
                      files={[]}
                      allowMultiple={true}
                      onUpload={(files: globalThis.File[]) => {
                        handleClaimDocumentUpload(files, values.attachments, setFieldValue);
                      }}
                      accept="application/pdf,.pdf,.png,.jpg,.jpeg"
                    />
                  </div>

                  {values.attachments.length > 0 && (
                    <div className="border border-border rounded-md p-3 mb-4">
                      <div className="text-sm font-medium text-fg-secondary mb-2 flex items-center">
                        <PaperclipIcon className="w-4 h-4 mr-1" />
                        Geüploade bijlagen ({values.attachments.length})
                      </div>

                      <div className="space-y-2">
                        {values.attachments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between bg-surface-sunken rounded p-2">
                            <div className="flex items-center flex-1">
                              <PaperclipIcon className="w-4 h-4 text-fg-muted mr-2" />
                              <span className="text-sm text-fg-secondary truncate">{doc.name}</span>
                            </div>

                            <div className="flex items-center space-x-2">
                              <Button
                                color="secondary"
                                onTrigger={() => downloadFile(doc)}
                                isLoading={downloadingFiles.includes(doc.id)}
                              >
                                Download
                              </Button>
                              <Button
                                color="danger"
                                onTrigger={() => {
                                  setFieldValue(
                                    'attachments',
                                    values.attachments.filter((a) => a.id !== doc.id),
                                  );
                                }}
                              >
                                <XIcon className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <Button type="submit" color="primary" isDisabled={isSubmitting} isLoading={isSubmitting}>
                    Claim aanmaken
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default CreateClaimPage;
